<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'

import InvestForm, { type PlaceOrderPayload, type Calculator } from './InvestForm.vue'
import InvestEligibilityBlocker, { type EligibilityFix } from './InvestEligibilityBlocker.vue'
import InvestSuccessPanel from './InvestSuccessPanel.vue'

import type { PaymentMethod } from './PaymentMethodSelector.vue'

export interface PlacedOrder {
  id: string
  orderReference: string
  tokens: number
  totalPayable: number
  walletAmount: number
  pgAmount: number
  paymentMethod: PaymentMethod
}

interface Props {
  loading: boolean
  propertyError: boolean
  state: 'form' | 'submitting' | 'success'
  placedOrder: PlacedOrder | null
  eligibilityFix: EligibilityFix | null
  feesError: string | null
  galleryMainImage: string
  headerTitle: string
  headerLocation: string
  calculator: Calculator
  walletBalance: number
  orderError: string | null
  isInsufficientBalance: boolean
  formInstanceKey: number
  tokenCount: number
}

defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:tokenCount', value: number): void
  (e: 'eligibility-cta'): void
  (e: 'place-order', payload: PlaceOrderPayload): void
  (e: 'open-terms'): void
  (e: 'view-receipt'): void
  (e: 'success-done'): void
  (e: 'close'): void
  (e: 'add-funds'): void
}>()
</script>

<template>
  <Box direction="col" class="invest-panel">
    <!-- Loading -->
    <Box v-if="loading" direction="col" gap="3" class="invest-panel__loading">
      <Skeleton animation="wave" width="100%" height="76px" border-radius="12px" />
      <Skeleton animation="wave" width="100%" height="92px" border-radius="12px" />
      <Skeleton animation="wave" width="100%" height="180px" border-radius="12px" />
      <Skeleton animation="wave" width="100%" height="120px" border-radius="12px" />
    </Box>

    <!-- Property fetch error -->
    <Box
      v-else-if="propertyError"
      direction="col"
      gap="3"
      align="center"
      justify="center"
      class="invest-panel__error"
    >
      <PiIcon name="exclamation-circle" class="invest-panel__error-icon" />
      <Text type="body" align="center">
        We couldn't load this property. Close and try again.
      </Text>
    </Box>

    <!-- Success -->
    <InvestSuccessPanel
      v-else-if="state === 'success' && placedOrder"
      :property-title="headerTitle"
      :tokens="placedOrder.tokens"
      :total-payable="placedOrder.totalPayable"
      :order-reference="placedOrder.orderReference"
      @view-receipt="emit('view-receipt')"
      @done="emit('success-done')"
    />

    <!-- Eligibility blocker -->
    <InvestEligibilityBlocker
      v-else-if="eligibilityFix"
      :fix="eligibilityFix"
      :property-image="galleryMainImage"
      :property-title="headerTitle"
      :property-location="headerLocation"
      @cta="emit('eligibility-cta')"
    />

    <!-- Form — the fees error is surfaced inline under the token stepper
         (see InvestForm) so the user can adjust the quantity and retry,
         rather than being kicked out to a dead-end error screen. -->
    <InvestForm
      v-else
      :key="`invest-form-${formInstanceKey}`"
      :property-image="galleryMainImage"
      :property-title="headerTitle"
      :property-location="headerLocation"
      :calculator="calculator"
      :wallet-balance="walletBalance"
      :submitting="state === 'submitting'"
      :error-message="orderError"
      :fees-error="feesError"
      :is-insufficient-balance="isInsufficientBalance"
      :token-count="tokenCount"
      @update:token-count="(v) => emit('update:tokenCount', v)"
      @place-order="(payload) => emit('place-order', payload)"
      @open-terms="emit('open-terms')"
      @add-funds="emit('add-funds')"
    />
  </Box>
</template>

<style scoped>
.invest-panel {
  min-height: 100%;
}

.invest-panel__loading {
  flex: 1;
  padding: 1.25rem 1.5rem 1rem;
}

.invest-panel__error {
  flex: 1;
  padding: 2rem 1.5rem;
}

.invest-panel__error-icon {
  font-size: 28px;
  color: var(--p-danger-500);
}
</style>
